import { useEffect, useMemo, useState } from 'react';
import { calculatePlan, isOutOfStockError } from '../services/api';
import { buildApiPayload } from '../utils/formPayload';
import { calculateCheckoutPricing } from '../utils/pricing';
import { monthlyDistributionFromPlan, selectedRecipesWithoutDropped } from '../utils/recipeDistribution';
import type { DogFormData, Recipe } from '../types';
import type { AppConfigResponse } from '../types/config';
import type { DroppedRecipeResponse, RecipeQuantityResponse } from '../api/calculate';

function isRecipeCatalogError(err: unknown): boolean {
  if (isOutOfStockError(err)) return true;
  if (!(err instanceof Error)) return false;
  return err.message.includes('no están disponibles') || err.message.includes('No se encontraron recetas válidas');
}

// Keep only the pack lines of the recipes that remain after a prune, so the summary never
// shows a dropped recipe while the re-run is in flight (or if it fails).
function pickDistribution(
  distribution: Record<string, number> | undefined,
  productIds: string[],
): Record<string, number> | undefined {
  if (!distribution) return undefined;
  const keep = new Set(productIds);
  return Object.fromEntries(Object.entries(distribution).filter(([productId]) => keep.has(productId)));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface UseCheckoutCalculationArgs {
  formData: Partial<DogFormData>;
  recipes: Recipe[];
  config: AppConfigResponse;
  isTransition: boolean;
  normalizedSelectedRecipeIds: string[];
  updateFormData: (patch: Partial<DogFormData>) => void;
  setCurrentStep: (step: number) => void;
  refreshConfig: (opts?: { silent?: boolean }) => Promise<unknown>;
}

export interface UseCheckoutCalculationResult {
  isCalculating: boolean;
  pricingError: Error | null;
  /** Recipes the latest plan could not serve: the selection was pruned and the plan re-run. */
  droppedRecipes: DroppedRecipeResponse[];
  /** Recipes the latest plan moved to their other size for stock, with a product the tutor
   *  had not seen in the pack yet (the summary shows the new size, so say why). */
  sizeChanges: SizeChangeResponse[];
}

export type SizeChangeResponse = Pick<RecipeQuantityResponse, 'recipe_id' | 'recipe_name' | 'resolved_size_g'>;

export function useCheckoutCalculation({
  formData,
  recipes,
  config,
  isTransition,
  normalizedSelectedRecipeIds,
  updateFormData,
  setCurrentStep,
  refreshConfig,
}: UseCheckoutCalculationArgs): UseCheckoutCalculationResult {
  const [isCalculating, setIsCalculating] = useState(false);
  const [pricingError, setPricingError] = useState<Error | null>(null);
  const [droppedRecipes, setDroppedRecipes] = useState<DroppedRecipeResponse[]>([]);
  const [sizeChanges, setSizeChanges] = useState<SizeChangeResponse[]>([]);

  const requestKey = useMemo(
    () =>
      JSON.stringify([
        formData.animalType,
        formData.nombre,
        formData.raza,
        formData.ageCategory,
        formData.ageYears,
        formData.ageMonths,
        formData.ageTotalMonths,
        formData.peso,
        formData.bodyCondition,
        formData.activityLevel,
        formData.isNeutered,
        formData.pathologies,
        formData.subPathologies,
        formData.currentFood,
        formData.appetiteType,
        formData.proteinAllergies,
        formData.dietType,
        formData.sizeChoice,
        formData.recipeEnergyDistribution,
        // Order is irrelevant to the plan; sorting avoids a re-run (which would wipe a
        // manual pack edit) when only the order of the ids changes.
        [...normalizedSelectedRecipeIds].sort(),
      ]),
    [
      formData.animalType,
      formData.nombre,
      formData.raza,
      formData.ageCategory,
      formData.ageYears,
      formData.ageMonths,
      formData.ageTotalMonths,
      formData.peso,
      formData.bodyCondition,
      formData.activityLevel,
      formData.isNeutered,
      formData.pathologies,
      formData.subPathologies,
      formData.currentFood,
      formData.appetiteType,
      formData.proteinAllergies,
      formData.dietType,
      formData.sizeChoice,
      formData.recipeEnergyDistribution,
      normalizedSelectedRecipeIds,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;

    function clearPendingTimer(): void {
      if (pendingTimer !== null) {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      }
    }

    async function hydratePricing(): Promise<void> {
      updateFormData({ calculationId: undefined });
      setPricingError(null);
      // 150 ms delay suppresses the spinner for cache-hit responses (< 150 ms).
      // Promises (microtasks) resolve before a setTimeout(0) macrotask, so 0 ms
      // would never actually hide the spinner — use an intentional delay instead.
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        if (!cancelled) setIsCalculating(true);
      }, 150);
      try {
        const payload = buildApiPayload(formData, {
          selectedRecipes: normalizedSelectedRecipeIds,
          platform: config.effective_platform,
        });
        // /calculate-plan (stateless): este recálculo corre al entrar al
        // checkout, ANTES de que exista email — no debe persistir nada. El
        // cálculo persistido (con el email real) lo crea el submit de Step13.
        let calculation;
        try {
          calculation = await calculatePlan(payload);
        } catch (firstErr) {
          if (cancelled || isRecipeCatalogError(firstErr)) throw firstErr;
          await delay(650);
          if (cancelled) return;
          calculation = await calculatePlan(payload);
        }
        if (cancelled) return;
        const backendMonthlyDiscountedPrice = Number(calculation.monthly_price.toFixed(2));
        if (backendMonthlyDiscountedPrice === 0 || !calculation.recipes || calculation.recipes.length === 0) {
          updateFormData({
            selectedRecipes: [],
            calculationId: undefined,
            recipeStockBounced: true,
          });
          // Paso 10 = selector de recetas (Step10_Recetas_v2): es donde se
          // renderiza el banner de stock-bounce. Antes rebotaba al 11 (Tu
          // Plan) sin recetas ni explicación.
          setCurrentStep(10);
          return;
        }
        // Sizes the backend changed for stock since the pack the tutor last saw.
        const shownProductIds = new Set(Object.keys(formData.initiationPackDistribution ?? {}));
        setSizeChanges(
          calculation.recipes
            .filter(
              (recipe) =>
                recipe.size_reason === 'stock_fallback' &&
                recipe.resolved_size_g &&
                !shownProductIds.has(recipe.recipe_id),
            )
            .map((recipe) => ({
              recipe_id: recipe.recipe_id,
              recipe_name: recipe.recipe_name,
              resolved_size_g: recipe.resolved_size_g,
            })),
        );
        const dropped = calculation.dropped_recipes ?? [];
        if (dropped.length > 0) {
          setDroppedRecipes(dropped);
          const remaining = selectedRecipesWithoutDropped(normalizedSelectedRecipeIds, dropped, recipes);
          if (remaining.length > 0 && remaining.length < normalizedSelectedRecipeIds.length) {
            // Prune the selection; the re-run hydrates prices for the recipes that remain.
            updateFormData({
              selectedRecipes: remaining,
              calculationId: undefined,
              packDistributionEdited: false,
              initiationPackDistribution: pickDistribution(formData.initiationPackDistribution, remaining),
              recommendedInitiationPackDistribution: pickDistribution(
                formData.recommendedInitiationPackDistribution,
                remaining,
              ),
            });
            return;
          }
        }
        const monthlyDistribution = monthlyDistributionFromPlan(calculation.recipes);
        const nextDistribution = Object.fromEntries(
          calculation.recipes
            .map((recipe) => [recipe.recipe_id, isTransition ? recipe.initiation_cans : recipe.monthly_cans] as const)
            .filter(([, quantity]) => quantity > 0),
        );
        const hydrated = calculateCheckoutPricing({
          initiationPackDistribution: nextDistribution,
          initiationPackPrice: isTransition ? calculation.initiation_pack_price : calculation.monthly_price,
          monthlyPlanPrice: backendMonthlyDiscountedPrice,
          supplementsOneOffTotal: formData.supplementsOneOffTotal,
          recipes,
          config,
          isInitiation: isTransition,
        });
        // Sin calculationId: el plan stateless no persiste. El submit de
        // Step13 ve calculationId undefined y crea el cálculo definitivo.
        updateFormData({
          initiationPackPrice: hydrated.initiationPrice,
          monthlyPlanPrice: backendMonthlyDiscountedPrice,
          initiationPackDistribution: nextDistribution,
          recommendedInitiationPackDistribution: nextDistribution,
          monthlyPlanDistribution: monthlyDistribution,
          // A fresh recommendation supersedes any manual pack edit.
          packDistributionEdited: false,
          monthlyPackSummary: calculation.monthly_pack ?? undefined,
          initiationPackSummary: calculation.initiation_pack ?? undefined,
          packConservation: calculation.pack_conservation ?? undefined,
          ...(calculation.pack_conservation
            ? { globalSizeChoice: calculation.pack_conservation.resolved_pack_size_g === 800 ? '800g' : '400g' }
            : {}),
        });
      } catch (err) {
        if (cancelled) return;
        if (isRecipeCatalogError(err)) {
          updateFormData({ selectedRecipes: [], calculationId: undefined, recipeStockBounced: true });
          try {
            await refreshConfig({ silent: true });
          } catch (refreshErr) {
            console.error('refreshConfig failed during recipe-catalog bounce:', refreshErr);
          }
          if (cancelled) return;
          setCurrentStep(10);
          return;
        }
        const normalizedErr = err instanceof Error ? err : new Error('Pricing preview error');
        console.error('Pricing preview error:', normalizedErr);
        setPricingError(normalizedErr);
      } finally {
        clearPendingTimer();
        if (!cancelled) setIsCalculating(false);
      }
    }

    hydratePricing();

    return () => {
      cancelled = true;
      clearPendingTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  return { isCalculating, pricingError, droppedRecipes, sizeChanges };
}
